import { createApiClient, createTokenStorage } from '@space/shared-api-client'
import { handleJSMessage } from '@/shared/utils/jsMessageHandler'
import { LOGIN_SESSION_EXPIRED } from '@/shared/utils/captchaRetryService'
import { captureEmailCode } from '@/shared/utils/devEmailCodeCapture'
import { assertCurrentAuthRequest } from '@/shared/api/authRequestGuard'

/**
 * Determine API base URL:
 * 1. Use window.__API_BASE_URL__ if set (configured in HTML at build time)
 * 2. Use VITE_GONKAGAME_API_URL env variable (for development override)
 * 3. Fall back to relative /api (works with same-origin requests)
 */
export const getApiBaseUrl = (): string => {
  // Production: use build-time configured API endpoint from HTML
  if (typeof window !== 'undefined' && (window as any).__API_BASE_URL__) {
    return (window as any).__API_BASE_URL__
  }

  // Development: allow env override
  if (import.meta.env.VITE_GONKAGAME_API_URL) {
    return import.meta.env.VITE_GONKAGAME_API_URL
  }

  // Default: relative /api for all domains (same-origin requests)
  return '/api'
}

const baseURL = getApiBaseUrl()
const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken'

const tokenStorage = createTokenStorage({ storageKey })

/**
 * Handle 401 Unauthorized: clear auth state and conditionally redirect.
 * On public routes (docs, events, landing) — just clear the token silently.
 * On protected routes — redirect to landing page.
 */
const handleUnauthorized = () => {
  console.warn('[gonkagame] 401 Unauthorized - logging out user')

  // Clear all auth-related data
  tokenStorage.setToken('')
  localStorage.removeItem(storageKey)
  localStorage.removeItem('user_avatar')
  localStorage.removeItem('partner_bonus_split')
  localStorage.removeItem('is_ambassador')

  // Clear matrix module localStorage cache (CR-MTX-07)
  Object.keys(localStorage)
    .filter((key) => key.startsWith('/api/matrix/'))
    .forEach((key) => localStorage.removeItem(key))

  // On public routes, just clear the stale token — don't redirect.
  // Use pathname check instead of router import to avoid circular module dependencies.
  const publicPrefixes = [
    '/docs',
    '/events',
    '/event-reward',
    '/r/',
    '/registration',
    '/tma-init',
  ]
  const isPublicPath = publicPrefixes.some((p) =>
    window.location.pathname.startsWith(p),
  )
  if (
    !isPublicPath &&
    window.location.pathname !== '/' &&
    window.location.pathname !== '/nyro-art'
  ) {
    window.location.href = '/'
  }
}

const isMiniAppEndpoint = (endpoint?: string): boolean =>
  !!endpoint &&
  (endpoint.startsWith('/telegram/mini-app/') ||
    endpoint === '/auth/exchange-token')

const isMiniAppPayload = (context: unknown): boolean => {
  if (
    !context ||
    typeof context !== 'object' ||
    !('endpoint' in context) ||
    typeof context.endpoint !== 'string'
  )
    return false
  return isMiniAppEndpoint(context.endpoint)
}

const client = createApiClient({
  baseURL,
  tokenStorage,
  spaceId: import.meta.env.VITE_X_SPACE_ID,
  onUnauthorized: handleUnauthorized,
  logger: {
    debug: (message, context) => {
      if (isMiniAppPayload(context)) return
      // Payment payloads/URLs stay private even in development. Keep other logging intact.
      if (
        context &&
        typeof context === 'object' &&
        'endpoint' in context &&
        typeof context.endpoint === 'string' &&
        (context.endpoint === '/profile' ||
          context.endpoint.startsWith('/payment/gonka-keeper/') ||
          context.endpoint.startsWith('/payment/depay/') ||
          context.endpoint === '/wallet/topup' ||
          context.endpoint.startsWith('/payment/portal/') ||
          context.endpoint.startsWith('/profile/nyro/') ||
          context.endpoint.startsWith('/profile/sso/') ||
          context.endpoint === '/profile/ami-handoff' ||
          context.endpoint === '/profile/igra-vpn-handoff' ||
          context.endpoint.startsWith('/providers/aita/') ||
          context.endpoint.startsWith('/wallet/utp-exchange/') ||
          context.endpoint === '/wallet/send' ||
          context.endpoint === '/wallet/withdraw-v2')
      )
        return
      if (import.meta.env.DEV) console.debug('[api-client]', message, context)
    },
  },
  // @ts-ignore: Assuming underlying client supports axios config merging or specific option
  withCredentials: true,
  headers: {
    'x-expect-format': 'json',
  },
})

// OTP-R3: opt-in auth requests keep shared transport/parsing, but cannot mutate
// auth before their owner accepts the parsed response. Outgoing auth reads live storage.
const guardedClient = createApiClient({
  baseURL,
  spaceId: import.meta.env.VITE_X_SPACE_ID,
  tokenStorage: {
    getToken: () => tokenStorage.getToken(),
    setToken: () => {},
    clearToken: () => {},
  },
  // One logger for both privacy fences: MiniApp payloads (debug) and GG points (debug + error).
  logger: {
    debug: (...args) => {
      if (isMiniAppPayload(args[1]) || isGgPointsLog(args)) return
      if (import.meta.env.DEV) console.debug('[api-client]', ...args)
    },
    error: (...args) => {
      if (isGgPointsLog(args)) return
      console.error('[api-client]', ...args)
    },
  },
})

function isGgPointsLog(args: unknown[]): boolean {
  return args.some(
    (value) =>
      !!value &&
      typeof value === 'object' &&
      'endpoint' in value &&
      typeof value.endpoint === 'string' &&
      value.endpoint.startsWith('/gg-points/'),
  )
}

const CODE_FORM_ENDPOINTS = [
  '/profile/login-by-code',
  '/profile/login-by-code/verify',
  '/profile/login-approval',
  '/profile/login-approval/send',
  '/profile/recovery-pass',
  '/profile/recovery-pass/check',
]

type AuthResponseAccepted = (accepted: {
  token: string | null
  ok: boolean
  data: unknown
}) => void

const acceptResponseToken = (
  response: Response,
  data: unknown,
  onAccepted?: AuthResponseAccepted,
) => {
  const token =
    response.headers.get('x-token') ?? response.headers.get('x-auth')
  // Synchronous provenance/promotion bookkeeping, before any notification effects.
  onAccepted?.({ token, ok: response.ok, data })
  if (token) tokenStorage.setToken(token)
}

/**
 * Wrap client methods to intercept JSMessage in responses and handle 401
 */
const wrapClient = (apiClient: typeof client) => {
  const originalGet = apiClient.get.bind(apiClient)
  const originalPost = apiClient.post.bind(apiClient)
  const originalPut = apiClient.put.bind(apiClient)
  const originalPatch = apiClient.patch.bind(apiClient)
  const originalDelete = apiClient.delete.bind(apiClient)
  const originalRequestRaw = apiClient.requestRaw.bind(apiClient)

  const intercept = async <T>(
    promise: Promise<T>,
    isCurrent?: () => boolean,
    onAccepted?: AuthResponseAccepted,
    endpoint?: string,
  ): Promise<T> => {
    const isProductHandoff =
      endpoint === '/profile/ami-handoff' ||
      endpoint === '/profile/igra-vpn-handoff' ||
      endpoint?.startsWith('/profile/sso/')
    // The GG card shows its own state; server messages for it never become toasts.
    const isGgPoints = !!endpoint?.startsWith('/gg-points/')
    try {
      const result = await promise
      assertCurrentAuthRequest(isCurrent)
      if (!isProductHandoff && !isGgPoints && !isMiniAppEndpoint(endpoint))
        handleJSMessage(result)
      return result
    } catch (error: any) {
      assertCurrentAuthRequest(isCurrent)
      // MiniApp errors are localized by their owner; never toast raw server diagnostics.
      if (isMiniAppEndpoint(endpoint)) {
        // The shared fetch client throws before parsing non-2xx bodies. Preserve
        // the envelope for the owner, but never promote headers or toast it.
        if (error?.response instanceof Response) {
          try {
            error.response.data = await error.response.clone().json()
          } catch {
            /* non-JSON stays a transport error */
          }
        }
        throw error
      }
      // Default requests retain their existing immediate 401 handling.
      const status = error?.response?.status ?? error?.status
      if (status === 401 && !isCurrent) {
        handleUnauthorized()
        throw error
      }

      if (error?.response) {
        // Many legacy errors come as 4xx/5xx with JSMessage in body
        try {
          const data = await error.response.json()
          assertCurrentAuthRequest(isCurrent)
          if (isCurrent) acceptResponseToken(error.response, data, onAccepted)
          // Like the default path, 401 does not toast or attach parsed data.
          if (status === 401) throw error

          // Check for LOGIN_SESSION_EXPIRED (error_code: 22022)
          const isSessionExpired =
            data?.error_code === LOGIN_SESSION_EXPIRED ||
            data?.errorCode === LOGIN_SESSION_EXPIRED
          if (isSessionExpired) {
            console.warn(
              '[gonkagame] Login session expired - clearing auth and reloading',
            )

            // Clear all auth-related data
            tokenStorage.setToken('')
            localStorage.removeItem(storageKey)
            localStorage.removeItem('user_avatar')
            localStorage.removeItem('partner_bonus_split')
            localStorage.removeItem('is_ambassador')

            // Clear auth cookie
            document.cookie =
              'dasdasdaddjdj=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'

            // Reload application to reset state
            window.location.reload()
            throw error
          }

          // Skip toast for errors that frontend handles specially
          const isCaptchaExpired =
            data?.error_code === 11011 || data?.errorCode === 11011
          const isEmailExists =
            data?.error_code === 11012 || data?.errorCode === 11012
          // Marketplace ownership/approval reconciliation errors come back as a
          // generic 400 with English `error_message`. The buy-modal localises
          // and presents these — the auto-toast here would steal the slot with
          // an English JSMessage. Skip on substring match.
          const rawMsg =
            typeof data?.error_message === 'string'
              ? data.error_message.toLowerCase()
              : typeof data?.error_details === 'string'
                ? data.error_details.toLowerCase()
                : ''
          const isMarketplaceFlowError =
            rawMsg.includes('seller no longer holds') ||
            rawMsg.includes('no longer owns') ||
            rawMsg.includes('seller has revoked') ||
            rawMsg.includes('approval revoked') ||
            rawMsg.includes('left escrow custody') ||
            rawMsg.includes('listing has been cancelled') ||
            rawMsg.includes('cannot purchase your own')
          // R6c: лимит кодов из приложения (31) окно входа пишет под полем само,
          // тост с текстом сервера повторил бы то же самое второй раз.
          const isShownByTotpStep =
            endpoint === '/profile/login-approval' && data?.error_code === 31
          // Лимиты кодов (32 — код сожжён, 33 — слишком много запросов) окна входа
          // и восстановления тоже пишут у поля кода сами.
          const isShownByCodeStep =
            (data?.error_code === 32 || data?.error_code === 33) &&
            CODE_FORM_ENDPOINTS.includes(endpoint ?? '')
          const isTelegramDeliveryFailure =
            status === 400 &&
            data?.result === 'error' &&
            data?.error_type === 'telegram_delivery_failed' &&
            (endpoint === '/profile/login-by-code' ||
              endpoint === '/profile/recovery-pass')
          // Корзина потоков отклоняется ДО оплаты с машиночитаемым `reason`;
          // `error_message` — английский текст для логов. Человеку причину
          // показывает модалка оплаты по-русски, авто-тост тут только мешает.
          const isBasketRejected = rawMsg.startsWith('basket rejected, reason')
          const isSponsorAssignment = endpoint === '/profile/assign-sponsor'
          if (
            !isProductHandoff &&
            !isGgPoints &&
            !isCaptchaExpired &&
            !isEmailExists &&
            !isMarketplaceFlowError &&
            !isShownByTotpStep &&
            !isShownByCodeStep &&
            !isTelegramDeliveryFailure &&
            !isBasketRejected &&
            !isSponsorAssignment
          ) {
            handleJSMessage(data)
          }
          // Attach parsed data to error for downstream handlers
          error.response.data = data
        } catch (e) {
          assertCurrentAuthRequest(isCurrent)
          // Fallback if not json
        }
      }
      assertCurrentAuthRequest(isCurrent)
      if (status === 401 && isCurrent) handleUnauthorized()
      throw error
    }
  }

  const guardedRequest = <T>(
    endpoint: string,
    isCurrent: () => boolean,
    options?: Parameters<typeof client.requestRaw>[1],
    onAccepted?: AuthResponseAccepted,
  ): Promise<T> => {
    const request = async () => {
      assertCurrentAuthRequest(isCurrent)
      const { data, response } = await guardedClient.requestRaw<T>(
        endpoint,
        options,
      )
      assertCurrentAuthRequest(isCurrent)
      // Only the MiniApp controller may store a validated exchange body token.
      if (!isMiniAppEndpoint(endpoint))
        acceptResponseToken(response, data, onAccepted)
      return data
    }
    return intercept(request(), isCurrent, onAccepted, endpoint)
  }

  /**
   * Intercept raw requests and capture email-code header (dev only)
   */
  const interceptRaw = async <T>(
    promise: Promise<{ data: T; response: Response }>,
  ): Promise<{ data: T; response: Response }> => {
    try {
      const result = await promise
      handleJSMessage(result.data)

      // [DEV ONLY] Capture email-code header from security API responses
      captureEmailCode(result.response)

      return result
    } catch (error: any) {
      // Check for 401 status and trigger logout
      const status = error?.response?.status ?? error?.status
      if (status === 401) {
        handleUnauthorized()
        throw error
      }

      if (error?.response) {
        try {
          const data = await error.response.json()
          handleJSMessage(data)
          error.response.data = data
        } catch (e) {
          // Fallback if not json
        }
      }
      throw error
    }
  }

  apiClient.get = (endpoint, options) =>
    intercept(originalGet(endpoint, options))
  apiClient.post = (endpoint, body, options) =>
    isMiniAppEndpoint(endpoint)
      ? guardedRequest(endpoint, () => true, {
          ...options,
          method: 'POST',
          body: JSON.stringify(body),
        })
      : intercept(
          originalPost(endpoint, body, options),
          undefined,
          undefined,
          endpoint,
        )
  apiClient.put = (endpoint, body, options) =>
    intercept(originalPut(endpoint, body, options))
  apiClient.patch = (endpoint, body, options) =>
    intercept(originalPatch(endpoint, body, options))
  apiClient.delete = (endpoint, options) =>
    intercept(originalDelete(endpoint, options))

  /**
   * POST request that returns both data and response (for header access)
   * Used for security endpoints that may include email-code header in dev mode
   */
  const postRaw = async <T>(
    endpoint: string,
    body?: unknown,
    options?: Parameters<typeof apiClient.post>[2],
  ): Promise<{ data: T; response: Response }> => {
    return interceptRaw<T>(
      originalRequestRaw<T>(endpoint, {
        ...options,
        method: 'POST',
        body: body as any,
      }),
    )
  }

  // Explicitly expose token methods (they exist on apiClient from createApiClient)
  return {
    ...apiClient,
    postRaw,
    guardedRequest,
    getToken: () => apiClient.getToken(),
    setToken: (token: string) => apiClient.setToken(token),
    clearToken: () => apiClient.clearToken(),
  }
}

export const httpClient = wrapClient(client)
